import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  CheckCircle, AlertCircle, Plus, Globe, ArrowLeft, Server,
  Activity, Upload, Download, FileText, XCircle,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useOrganization } from '../contexts/OrganizationContext';
import { supabaseHelpers } from '../lib/supabaseClient';

const AddEndpointForm = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const { currentOrganization } = useOrganization();

  const [services, setServices] = useState([]);
  const [showNewService, setShowNewService] = useState(false);
  const [formData, setFormData] = useState({
    label: '', url: '', expected_status_code: 200, timeout_ms: 10000,
    check_content_for: '', notification_email: '', priority: 'medium',
    is_active: true, service_id: '',
  });
  const [newService, setNewService] = useState({
    name: '', description: '', color: '#8B5CF6', priority: 'medium',
  });
  const [isLoading, setIsLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  // Bulk upload state
  const MAX_BULK_ROWS = 100;
  const [mode, setMode] = useState('single');
  const [bulkFormat, setBulkFormat] = useState('csv');
  const [inputText, setInputText] = useState('');
  const [parsedRows, setParsedRows] = useState([]);
  const [bulkRowsTotal, setBulkRowsTotal] = useState(0);
  const [bulkServiceId, setBulkServiceId] = useState('');
  const [bulkProgress, setBulkProgress] = useState(null); // { done, total, errors: [] }
  const [bulkComplete, setBulkComplete] = useState(false);

  const fetchServices = useCallback(async () => {
    try {
      const servicesData = await supabaseHelpers.services.getServices(currentOrganization.id);
      setServices(servicesData);
      if (servicesData.length === 0) setShowNewService(true);
    } catch {
      setError('Failed to load services');
    }
  }, [currentOrganization]);

  useEffect(() => {
    if (currentOrganization) {
      fetchServices();
      const serviceId = searchParams.get('serviceId');
      if (serviceId) setFormData(prev => ({ ...prev, service_id: serviceId }));
    }
  }, [currentOrganization, searchParams, fetchServices]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleNewServiceChange = (e) => {
    const { name, value } = e.target;
    setNewService(prev => ({ ...prev, [name]: value }));
  };

  const handleCreateService = async () => {
    if (!newService.name.trim()) { setError('Service name is required'); return; }
    try {
      const createdService = await supabaseHelpers.services.createService({
        ...newService,
        organization_id: currentOrganization.id,
        created_by: user.id,
        is_active: true,
      });
      setServices(prev => [...prev, createdService]);
      setFormData(prev => ({ ...prev, service_id: createdService.id }));
      setShowNewService(false);
      setNewService({ name: '', description: '', color: '#8B5CF6', priority: 'medium' });
      await supabaseHelpers.auditLogs.createAuditLog({
        organization_id: currentOrganization.id,
        user_id: user.id,
        action: 'service.created',
        resource_type: 'service',
        resource_id: createdService.id,
        details: { name: newService.name },
      });
    } catch {
      setError('Failed to create service');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setSuccess(false);
    setError('');

    if (!formData.service_id) {
      setError('Please select or create a service');
      setIsLoading(false);
      return;
    }

    try {
      const createdEndpoint = await supabaseHelpers.endpoints.createEndpoint({
        ...formData,
        organization_id: currentOrganization.id,
        created_by: user.id,
      });
      await supabaseHelpers.auditLogs.createAuditLog({
        organization_id: currentOrganization.id,
        user_id: user.id,
        action: 'endpoint.created',
        resource_type: 'endpoint',
        resource_id: createdEndpoint.id,
        details: { label: formData.label, url: formData.url },
      });
      setSuccess(true);
      setFormData(prev => ({
        label: '', url: '', expected_status_code: 200, timeout_ms: 10000,
        check_content_for: '', notification_email: '', priority: 'medium',
        is_active: true, service_id: prev.service_id,
      }));
      setTimeout(() => navigate('/admin'), 2000);
    } catch (err) {
      setError(err.message || 'Failed to create endpoint');
    } finally {
      setIsLoading(false);
    }
  };

  // ── Bulk helpers ──────────────────────────────────────────────────────
  const normalizeAndValidate = (raw) => {
    const errors = [];
    if (!raw.label) errors.push('Label required');
    if (!raw.url) errors.push('URL required');
    else if (!raw.url.startsWith('http')) errors.push('URL must start with http');
    return {
      label: raw.label || '',
      url: raw.url || '',
      notification_email: raw.notification_email || '',
      expected_status_code: parseInt(raw.expected_status_code) || 200,
      timeout_ms: parseInt(raw.timeout_ms) || 10000,
      priority: ['low','medium','high','critical'].includes(raw.priority) ? raw.priority : 'medium',
      errors,
    };
  };

  const parseCSV = (text) => {
    const lines = text.trim().split('\n').filter(l => l.trim());
    if (lines.length < 2) return [];
    const headers = lines[0].split(',').map(h => h.trim().toLowerCase());
    return lines.slice(1).map(line => {
      const values = line.split(',').map(v => v.trim());
      const raw = {};
      headers.forEach((h, i) => { raw[h] = values[i] || ''; });
      return normalizeAndValidate(raw);
    });
  };

  const parseJSON = (text) => {
    try {
      const arr = JSON.parse(text);
      if (!Array.isArray(arr)) return [{ errors: ['JSON must be an array'] }];
      return arr.map(normalizeAndValidate);
    } catch {
      return [{ errors: ['Invalid JSON'] }];
    }
  };

  const handleBulkTextChange = (text) => {
    setInputText(text);
    if (!text.trim()) { setParsedRows([]); setBulkRowsTotal(0); return; }
    const rows = bulkFormat === 'csv' ? parseCSV(text) : parseJSON(text);
    setBulkRowsTotal(rows.length);
    setParsedRows(rows.slice(0, MAX_BULK_ROWS));
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const text = ev.target.result;
      setInputText(text);
      const rows = bulkFormat === 'csv' ? parseCSV(text) : parseJSON(text);
      setBulkRowsTotal(rows.length);
      setParsedRows(rows.slice(0, MAX_BULK_ROWS));
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  const triggerDownload = (content, filename, mimeType) => {
    const blob = new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = filename; a.click();
    URL.revokeObjectURL(url);
  };

  const downloadCSVTemplate = () => {
    triggerDownload(
      'label,url,notification_email,expected_status_code,timeout_ms,priority\nLogin API,https://api.example.com/health,alerts@example.com,200,10000,medium',
      'endpoints-template.csv', 'text/csv'
    );
  };

  const downloadJSONTemplate = () => {
    triggerDownload(
      JSON.stringify([{ label: 'Login API', url: 'https://api.example.com/health', notification_email: 'alerts@example.com', expected_status_code: 200, timeout_ms: 10000, priority: 'medium' }], null, 2),
      'endpoints-template.json', 'application/json'
    );
  };

  const handleBulkSubmit = async () => {
    const valid = parsedRows.filter(r => r.errors.length === 0);
    if (valid.length === 0 || !bulkServiceId) return;

    setBulkProgress({ done: 0, total: valid.length, errors: [] });
    setBulkComplete(false);

    const rowErrors = [];

    for (let i = 0; i < valid.length; i++) {
      const { errors: _e, ...row } = valid[i];
      try {
        const ep = await supabaseHelpers.endpoints.createEndpoint({
          ...row,
          service_id: bulkServiceId,
          organization_id: currentOrganization.id,
          created_by: user.id,
          is_active: true,
        });
        await supabaseHelpers.auditLogs.createAuditLog({
          organization_id: currentOrganization.id,
          user_id: user.id,
          action: 'endpoint.created',
          resource_type: 'endpoint',
          resource_id: ep.id,
          details: { label: row.label, url: row.url, bulk: true },
        }).catch(() => {}); // audit log failure is non-fatal
      } catch (err) {
        rowErrors.push({ row: i + 1, label: row.label, message: err.message });
      }
      setBulkProgress(prev => ({ ...prev, done: i + 1, errors: rowErrors }));
    }

    setBulkComplete(true);

    if (rowErrors.length === 0) {
      setTimeout(() => navigate('/admin'), 2000);
    }
    // If there were errors, stay on the page so user can see the summary
  };

  const validCount = parsedRows.filter(r => r.errors.length === 0).length;
  const invalidCount = parsedRows.filter(r => r.errors.length > 0).length;
  const csvPlaceholder = 'label,url,notification_email,expected_status_code,timeout_ms,priority\nLogin API,https://api.example.com/health,alerts@co.com,200,10000,medium';
  const jsonPlaceholder = JSON.stringify([{ label: 'Login API', url: 'https://api.example.com/health', notification_email: 'alerts@co.com', expected_status_code: 200, timeout_ms: 10000, priority: 'medium' }], null, 2);

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#003F5C] via-[#3D4752] to-[#5E4B8B] p-8">
      <div className="max-w-3xl mx-auto">
        <div className="mb-8">
          <button onClick={() => navigate('/admin')}
            className="inline-flex items-center gap-2 text-gray-300 hover:text-white mb-4 transition-colors">
            <ArrowLeft className="w-4 h-4" />Back to Dashboard
          </button>
          <h1 className="text-3xl font-bold text-white mb-2">Add New Endpoint</h1>
          <p className="text-gray-300">Monitor a new API endpoint or service</p>
        </div>

        {/* Mode Tabs */}
        <div className="flex gap-1 mb-6 bg-white/5 rounded-xl p-1 border border-white/10 w-fit">
          {['single', 'bulk'].map(m => (
            <button key={m} onClick={() => setMode(m)}
              className={`flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-medium transition-all ${mode === m ? 'bg-[#2C9C7A] text-white shadow' : 'text-gray-400 hover:text-white'}`}>
              {m === 'bulk' && <Upload className="w-4 h-4" />}
              {m === 'single' ? 'Single Endpoint' : 'Bulk Upload'}
            </button>
          ))}
        </div>

        {/* ── Single ── */}
        {mode === 'single' && (
          <div className="bg-white/5 backdrop-blur-md rounded-2xl border border-white/10 p-8">
            {success && (
              <div className="mb-6 p-4 bg-[#2C9C7A]/10 border border-emerald-500/20 rounded-lg flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-[#2C9C7A] flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-[#2C9C7A] font-medium">Endpoint added successfully!</p>
                  <p className="text-[#2C9C7A]/80 text-sm mt-1">Redirecting to dashboard...</p>
                </div>
              </div>
            )}
            {error && (
              <div className="mb-6 p-4 bg-red-500/10 border border-red-500/20 rounded-lg flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                <p className="text-red-300 text-sm">{error}</p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Service Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Service <span className="text-red-400">*</span>
                </label>
                {!showNewService ? (
                  <div className="flex gap-2">
                    <select name="service_id" value={formData.service_id} onChange={handleChange} required
                      className="flex-1 px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-[#2C9C7A] focus:border-transparent transition-all">
                      <option value="" className="bg-gray-900">Select a service</option>
                      {services.map(s => <option key={s.id} value={s.id} className="bg-gray-900">{s.name}</option>)}
                    </select>
                    <button type="button" onClick={() => setShowNewService(true)}
                      className="flex items-center gap-2 px-4 py-3 bg-[#2C9C7A] hover:bg-[#168980] text-white rounded-lg transition-colors">
                      <Plus className="w-5 h-5" />New Service
                    </button>
                  </div>
                ) : (
                  <div className="space-y-4 p-4 bg-white/5 rounded-lg border border-white/10">
                    <div className="flex items-center justify-between">
                      <h4 className="text-white font-medium flex items-center gap-2">
                        <Server className="w-4 h-4" />Create New Service
                      </h4>
                      <button type="button" onClick={() => setShowNewService(false)} className="text-gray-400 hover:text-white text-sm">Cancel</button>
                    </div>
                    <div>
                      <label className="block text-sm text-gray-300 mb-2">Service Name <span className="text-red-400">*</span></label>
                      <input type="text" name="name" value={newService.name} onChange={handleNewServiceChange}
                        className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#2C9C7A]"
                        placeholder="e.g., Authentication API" />
                    </div>
                    <div>
                      <label className="block text-sm text-gray-300 mb-2">Description</label>
                      <input type="text" name="description" value={newService.description} onChange={handleNewServiceChange}
                        className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#2C9C7A]"
                        placeholder="Optional description" />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm text-gray-300 mb-2">Color</label>
                        <input type="color" name="color" value={newService.color} onChange={handleNewServiceChange}
                          className="w-full h-10 px-2 bg-white/5 border border-white/10 rounded-lg cursor-pointer" />
                      </div>
                      <div>
                        <label className="block text-sm text-gray-300 mb-2">Priority</label>
                        <select name="priority" value={newService.priority} onChange={handleNewServiceChange}
                          className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-[#2C9C7A]">
                          {['low','medium','high','critical'].map(p => <option key={p} value={p} className="bg-gray-900">{p.charAt(0).toUpperCase()+p.slice(1)}</option>)}
                        </select>
                      </div>
                    </div>
                    <button type="button" onClick={handleCreateService}
                      className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-[#2C9C7A] hover:bg-[#168980] text-white rounded-lg transition-colors">
                      <Plus className="w-4 h-4" />Create Service
                    </button>
                  </div>
                )}
              </div>

              <div>
                <label htmlFor="label" className="block text-sm font-medium text-gray-300 mb-2">Endpoint Label <span className="text-red-400">*</span></label>
                <input type="text" id="label" name="label" value={formData.label} onChange={handleChange} required
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#2C9C7A] focus:border-transparent transition-all"
                  placeholder="e.g., Login API" />
              </div>

              <div>
                <label htmlFor="url" className="block text-sm font-medium text-gray-300 mb-2">URL <span className="text-red-400">*</span></label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Globe className="w-5 h-5 text-gray-400" />
                  </div>
                  <input type="url" id="url" name="url" value={formData.url} onChange={handleChange} required
                    className="w-full pl-10 pr-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#2C9C7A] focus:border-transparent transition-all"
                    placeholder="https://api.example.com/health" />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="expected_status_code" className="block text-sm font-medium text-gray-300 mb-2">Expected Status Code</label>
                  <input type="number" id="expected_status_code" name="expected_status_code" value={formData.expected_status_code} onChange={handleChange}
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-[#2C9C7A] transition-all" />
                </div>
                <div>
                  <label htmlFor="timeout_ms" className="block text-sm font-medium text-gray-300 mb-2">Timeout (ms)</label>
                  <input type="number" id="timeout_ms" name="timeout_ms" value={formData.timeout_ms} onChange={handleChange}
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-[#2C9C7A] transition-all" />
                </div>
                <div>
                  <label htmlFor="priority" className="block text-sm font-medium text-gray-300 mb-2">Priority</label>
                  <select id="priority" name="priority" value={formData.priority} onChange={handleChange}
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-[#2C9C7A] transition-all">
                    {['low','medium','high','critical'].map(p => <option key={p} value={p} className="bg-gray-900">{p.charAt(0).toUpperCase()+p.slice(1)}</option>)}
                  </select>
                </div>
                <div>
                  <label htmlFor="notification_email" className="block text-sm font-medium text-gray-300 mb-2">Notification Email</label>
                  <input type="email" id="notification_email" name="notification_email" value={formData.notification_email} onChange={handleChange}
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#2C9C7A] transition-all"
                    placeholder="alerts@example.com" />
                </div>
              </div>

              <div>
                <label htmlFor="check_content_for" className="block text-sm font-medium text-gray-300 mb-2">
                  Check Content For{' '}
                  <span className="text-yellow-400 text-xs font-normal">(not yet implemented in monitor)</span>
                </label>
                <input type="text" id="check_content_for" name="check_content_for" value={formData.check_content_for} onChange={handleChange}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#2C9C7A] transition-all"
                  placeholder="String to check in response body" />
              </div>

              <div className="flex items-center gap-3">
                <input type="checkbox" id="is_active" name="is_active" checked={formData.is_active} onChange={handleChange}
                  className="w-4 h-4 text-purple-600 bg-white/5 border-white/10 rounded focus:ring-[#2C9C7A]" />
                <label htmlFor="is_active" className="text-sm text-gray-300">Active (start monitoring immediately)</label>
              </div>

              <button type="submit" disabled={isLoading || success}
                className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-[#2C9C7A] to-[#3599B8] hover:from-[#168980] hover:to-[#28738A] text-white font-semibold rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-[#2C9C7A]/20">
                {isLoading ? <><Activity className="w-5 h-5 animate-spin" />Adding endpoint...</>
                  : success ? <><CheckCircle className="w-5 h-5" />Endpoint added!</>
                  : <><Plus className="w-5 h-5" />Add Endpoint</>}
              </button>
            </form>
          </div>
        )}

        {/* ── Bulk ── */}
        {mode === 'bulk' && (
          <div className="bg-white/5 backdrop-blur-md rounded-2xl border border-white/10 p-8 space-y-6">
            {/* Completion summary */}
            {bulkComplete && bulkProgress && (
              <div className={`p-4 rounded-lg flex items-start gap-3 ${
                bulkProgress.errors.length === 0
                  ? 'bg-[#2C9C7A]/10 border border-emerald-500/20'
                  : 'bg-yellow-500/10 border border-yellow-500/20'
              }`}>
                {bulkProgress.errors.length === 0
                  ? <CheckCircle className="w-5 h-5 text-[#2C9C7A] flex-shrink-0 mt-0.5" />
                  : <AlertCircle className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
                }
                <div>
                  <p className={`font-medium ${bulkProgress.errors.length === 0 ? 'text-[#2C9C7A]' : 'text-yellow-300'}`}>
                    {bulkProgress.done - bulkProgress.errors.length} of {bulkProgress.total} endpoints created
                    {bulkProgress.errors.length > 0 && ` — ${bulkProgress.errors.length} failed`}
                  </p>
                  {bulkProgress.errors.length > 0 && (
                    <ul className="mt-2 space-y-1">
                      {bulkProgress.errors.map((e, i) => (
                        <li key={i} className="text-xs text-yellow-200">
                          Row {e.row} ({e.label}): {e.message}
                        </li>
                      ))}
                    </ul>
                  )}
                  {bulkProgress.errors.length === 0 && (
                    <p className="text-[#2C9C7A]/80 text-sm mt-1">Redirecting to dashboard...</p>
                  )}
                </div>
              </div>
            )}

            {/* Service dropdown */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Service <span className="text-red-400">*</span></label>
              <select value={bulkServiceId} onChange={e => setBulkServiceId(e.target.value)}
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-[#2C9C7A] focus:border-transparent transition-all">
                <option value="" className="bg-gray-900">Select a service</option>
                {services.map(s => <option key={s.id} value={s.id} className="bg-gray-900">{s.name}</option>)}
              </select>
            </div>

            {/* Format toggle */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Format</label>
              <div className="flex gap-1 bg-white/5 rounded-xl p-1 border border-white/10 w-fit">
                {['csv','json'].map(f => (
                  <button key={f} type="button" onClick={() => { setBulkFormat(f); setInputText(''); setParsedRows([]); setBulkRowsTotal(0); }}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${bulkFormat === f ? 'bg-[#3599B8] text-white shadow' : 'text-gray-400 hover:text-white'}`}>
                    <FileText className="w-4 h-4" />{f.toUpperCase()}
                  </button>
                ))}
              </div>
            </div>

            {/* Template + upload */}
            <div className="flex items-center gap-4">
              <button type="button" onClick={bulkFormat === 'csv' ? downloadCSVTemplate : downloadJSONTemplate}
                className="flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/20 text-gray-300 hover:text-white rounded-lg transition-colors text-sm border border-white/10">
                <Download className="w-4 h-4" />Download {bulkFormat.toUpperCase()} Template
              </button>
              <label className="flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/20 text-gray-300 hover:text-white rounded-lg transition-colors text-sm border border-white/10 cursor-pointer">
                <Upload className="w-4 h-4" />Upload File
                <input type="file" accept={bulkFormat === 'csv' ? '.csv' : '.json'} onChange={handleFileUpload} className="hidden" />
              </label>
            </div>

            {/* Textarea */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Paste {bulkFormat.toUpperCase()} data</label>
              <textarea value={inputText} onChange={e => handleBulkTextChange(e.target.value)} rows={8}
                placeholder={bulkFormat === 'csv' ? csvPlaceholder : jsonPlaceholder}
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-[#2C9C7A] transition-all resize-y" />
            </div>

            {bulkRowsTotal > MAX_BULK_ROWS && (
              <div className="p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg flex items-start gap-2">
                <AlertCircle className="w-4 h-4 text-yellow-400 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-yellow-300">
                  Your file contains <strong>{bulkRowsTotal} rows</strong>. Only the first <strong>{MAX_BULK_ROWS}</strong> will be imported.
                </p>
              </div>
            )}

            {/* Preview */}
            {parsedRows.length > 0 && (
              <div>
                <h3 className="text-sm font-semibold text-gray-300 mb-3">Preview</h3>
                <div className="overflow-x-auto rounded-lg border border-white/10">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-white/10 text-gray-400 text-xs uppercase tracking-wider">
                        <th className="px-3 py-2 text-left w-8">#</th>
                        <th className="px-3 py-2 text-left">Label</th>
                        <th className="px-3 py-2 text-left">URL</th>
                        <th className="px-3 py-2 text-left">Priority</th>
                        <th className="px-3 py-2 text-left">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {parsedRows.map((row, idx) => {
                        const isValid = row.errors.length === 0;
                        return (
                          <tr key={idx} className={`border-t border-white/5 ${isValid ? 'bg-emerald-500/5' : 'bg-red-500/5'}`}>
                            <td className="px-3 py-2 text-gray-500">{idx + 1}</td>
                            <td className="px-3 py-2 text-white">{row.label || '--'}</td>
                            <td className="px-3 py-2 text-gray-300 truncate max-w-[180px]">{row.url || '--'}</td>
                            <td className="px-3 py-2 text-gray-300 capitalize">{row.priority || '--'}</td>
                            <td className="px-3 py-2">
                              {isValid
                                ? <span className="flex items-center gap-1 text-emerald-400 text-xs font-medium"><CheckCircle className="w-3.5 h-3.5" />Valid</span>
                                : <span className="flex items-start gap-1 text-red-400 text-xs font-medium"><XCircle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" /><span>{row.errors.join(', ')}</span></span>
                              }
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <p className="mt-3 text-sm text-gray-400">
                  <span className="text-emerald-400 font-semibold">{validCount} valid</span>
                  {invalidCount > 0 && <>, <span className="text-red-400 font-semibold">{invalidCount} invalid</span></>}
                  {' '}— ready to import
                </p>
              </div>
            )}

            {/* Progress */}
            {bulkProgress && !bulkComplete && (
              <div>
                <p className="text-sm text-gray-300 mb-2">Creating {bulkProgress.done} of {bulkProgress.total}...</p>
                <div className="w-full bg-white/10 rounded-full h-2">
                  <div className="bg-[#2C9C7A] h-2 rounded-full transition-all duration-300"
                    style={{ width: `${(bulkProgress.done / bulkProgress.total) * 100}%` }} />
                </div>
              </div>
            )}

            <button type="button" onClick={handleBulkSubmit}
              disabled={validCount === 0 || !bulkServiceId || (bulkProgress !== null && !bulkComplete)}
              className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-[#2C9C7A] to-[#3599B8] hover:from-[#168980] hover:to-[#28738A] text-white font-semibold rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-[#2C9C7A]/20">
              <Upload className="w-5 h-5" />
              Create {validCount} Endpoint{validCount !== 1 ? 's' : ''}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AddEndpointForm;
